import { useMemo } from "react";
import { useGroupByName } from "./useGroupByName";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { useIsFairfleetAdmin } from "@/hooks/useIsFairfleetAdmin";
import { userGroupIds } from "@/utilities/user/userGroupIds";

export interface GroupPermission {
  granted: boolean;
  /** While this is true, `granted` is still `false` — do not take it as a refusal yet. */
  isLoading: boolean;
}

/**
 * Whether the signed-in user may do something that is gated on a Geotab group.
 *
 * This is how the rest of the application already decides such things: a named group under
 * "Fairfleet Services" is the permission, and belonging to it grants it. Staff are exempt, by the
 * one rule that decides that — see {@link useIsFairfleetAdmin}.
 *
 * Membership is compared by group id against the user's own groups, which means being scoped to a
 * parent group does not grant a permission held by a child. That is deliberate and matches the
 * existing rule.
 *
 * @param groupName - The group that stands for the permission.
 */
export function useGroupPermission(groupName: string): GroupPermission {
  const { me } = useGeotabClient();
  const isFairfleetAdmin = useIsFairfleetAdmin();

  const { data: group, isLoading } = useGroupByName(isFairfleetAdmin ? undefined : groupName);

  return useMemo(() => {
    if (isFairfleetAdmin) {
      return { granted: true, isLoading: false };
    }
    if (isLoading || !me) {
      return { granted: false, isLoading: true };
    }

    const granted = !!group?.id && userGroupIds(me).includes(group.id);
    return { granted, isLoading: false };
  }, [group?.id, isFairfleetAdmin, isLoading, me]);
}
